<template>
  <Card class="file-browser">
    <template #header>
      <div class="card-header-custom">
        <h6 class="mb-0 text-primary font-semibold">
          <i class="pi pi-folder-open me-2"></i>
          File Browser
        </h6>
      </div>
    </template>
    
    <template #content>
      <div class="browser-content position-relative">
      <!-- Processing overlay -->
      <div v-if="isProcessing" class="processing-overlay">
        <div class="processing-spinner">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Processing...</span>
          </div>
          <p class="mt-2 mb-0 text-primary fw-medium">Processing RAW files...</p>
        </div>
      </div>
      
      <div class="file-tree file-tree-container" v-if="!isLoading" :key="fileTreeKey">
        <div v-if="treeNodes.length === 0" class="p-3 text-center text-muted">
          <i class="fas fa-folder-open fa-2x mb-2"></i>
          <p>No RAW files or folders found in this directory</p>
        </div>
        
        <!-- PrimeVue Tree Component -->
        <Tree 
          v-else
          :value="treeNodes"
          v-model:expandedKeys="expandedKeys"
          v-model:selectionKeys="selectionKeys"
          selectionMode="checkbox"
          class="w-100"
          @nodeExpand="onNodeExpand"
          @nodeCollapse="onNodeCollapse"
          @nodeSelect="onNodeSelect"
          @nodeUnselect="onNodeUnselect"
        >
          <!-- Custom template for folder nodes -->
          <template #folder="slotProps">
            <div class="d-flex align-items-center">
              <i class="fas fa-folder folder-icon me-2"></i>
              <span class="flex-grow-1">{{ slotProps.node.label }}</span>
            </div>
          </template>

          <!-- Custom template for file nodes -->
          <template #file="slotProps">
            <div class="d-flex align-items-center" 
                 :class="{ 'dng-file-disabled': slotProps.node.data?.isDng }">
              <!-- File icon or thumbnail -->
              <div class="file-icon-container me-2">
                <img v-if="slotProps.node.data && slotProps.node.data.isRaw" 
                     :src="getThumbnailUrlWithAuth(slotProps.node.data.originalData.path)"
                     class="file-thumbnail"
                     :class="{ 'clickable-thumbnail': !slotProps.node.data.isDng }"
                     @error="onThumbnailError"
                     @load="onThumbnailLoad"
                     @click.stop="slotProps.node.data.isDng ? null : showImagePreview(slotProps.node.data.originalData)"
                     :title="slotProps.node.data.isDng ? 'DNG file (processed output)' : 'Click to preview ' + slotProps.node.label">
                <i v-else 
                   class="fas fa-image image-icon"></i>
              </div>
              
              <span class="flex-grow-1" 
                    :class="{ 'text-muted': slotProps.node.data?.isDng }">
                {{ getDisplayFilename(slotProps.node.label, slotProps.node.data?.isDng) }}
              </span>
              
              <div class="file-info-container">
                <span v-if="slotProps.node.data?.isDng" class="badge bg-secondary ms-3 me-2 small">Processed</span>
              </div>
            </div>
          </template>
        </Tree>
      </div>
      
      <div v-else class="p-3 text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading file tree...</p>
      </div>
      </div>
    </template>
  </Card>

  <!-- Image Preview Modal using Teleport to render at body level -->
  <Teleport to="body">
      <div v-if="showPreviewModal" class="modal-overlay" @click="closeImagePreview">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-image me-2"></i>
              {{ previewImageData?.name }}
            </h5>
            <button class="btn-close" @click="closeImagePreview" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="image-container">
              <img v-if="previewImageData" 
                   :src="getLargePreviewUrlWithAuth(previewImageData.path)"
                   :alt="previewImageData.name"
                   class="preview-image"
                   @error="onPreviewImageError"
                   @load="onPreviewImageLoad">
              <div v-if="previewImageLoading" class="loading-spinner">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading large preview...</p>
              </div>
            </div>
          </div>
          <div class="modal-footer d-none d-md-block">
            <div class="image-info">
              <span class="text-muted">
                Size: {{ previewImageData ? (previewImageData.size / 1024 / 1024).toFixed(1) : '0' }} MB
              </span>
              <span class="text-muted ms-3">
                Path: {{ getRelativePath(previewImageData?.path || '') }}
              </span>
            </div>
          </div>
        </div>
      </div>
  </Teleport>
</template>

<script>
import axios from 'axios'

export default {
  name: 'FileTreePrimeVue',
  props: {
    config: Object,
    loading: {
      type: Boolean,
      default: false
    },
    selectedFiles: {
      type: Array,
      default: () => []
    },
    isProcessing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['file-selected', 'file-deselected', 'error'],
  data() {
    return {
      apiBaseUrl: this.getApiBaseUrl(),
      fileTree: [],
      treeNodes: [],
      currentPath: '',
      expandedKeys: {},
      selectionKeys: {},
      fileTreeKey: 0,
      isLoading: false,
      nodeMap: new Map(), // Map to track node keys to original data
      showPreviewModal: false,
      previewImageData: null,
      previewImageLoading: false
    }
  },
  
  watch: {
    config: {
      handler(newConfig) {
        if (newConfig) {
          this.currentPath = newConfig.base_image_input_folder
          this.loadFileTree()
        }
      },
      immediate: true
    },
    selectedFiles: {
      handler(newSelectedFiles) {
        this.updateSelectionFromProps(newSelectedFiles)
      },
      deep: true
    }
  },

  mounted() {
    // Add keyboard event listener for ESC key to close modal
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeUnmount() {
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeydown)
    // Restore body scroll if component is destroyed while modal is open
    document.body.style.overflow = ''
  },
  
  methods: {
    getApiBaseUrl() {
      // For production, use the deployed API endpoint with /api prefix
      if (process.env.NODE_ENV === 'production') {
        return `${window.location.protocol}//${window.location.hostname}/api`
      }
      
      // For development, always use localhost:3000 (backend server) with /api prefix
      const protocol = window.location.protocol
      const hostname = window.location.hostname
      const port = '3000'
      
      // If accessing via localhost, keep localhost (for development on same machine)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:${port}/api`
      }
      
      // For mobile or other devices, use the actual IP address with port 3000
      return `${protocol}//${hostname}:${port}/api`
    },

    async loadFileTree() {
      const pathToLoad = this.currentPath || this.config?.base_image_input_folder
      if (!pathToLoad) {
        return
      }
      
      try {
        this.isLoading = true
        const response = await axios.get(`${this.apiBaseUrl}/file-tree?path=${encodeURIComponent(pathToLoad)}`)
        this.fileTree = response.data
        this.convertToTreeNodes()
      } catch (error) {
        this.$emit('error', 'Failed to load file tree: ' + error.message)
      } finally {
        this.isLoading = false
      }
    },

    convertToTreeNodes() {
      this.nodeMap.clear()
      this.treeNodes = this.convertItemsToNodes(this.fileTree)
      this.fileTreeKey++
    },

    convertItemsToNodes(items, parentPath = '') {
      return items.map((item, index) => {
        const nodeKey = `${parentPath}/${item.name}_${index}`
        const isFolder = item.type === 'folder'
        const isRawFile = !isFolder && this.isRawFile(item.name)
        const isDngFile = !isFolder && this.isDngFile(item.name)
        const isSelectableRaw = !isFolder && this.isSelectableRawFile(item.name)
        
        let node = {
          key: nodeKey,
          label: item.name,
          type: isFolder ? 'folder' : 'file',
          leaf: !isFolder,
          selectable: isFolder || isSelectableRaw, // Only folders and selectable RAW files are selectable
          data: {
            originalData: item,
            isRaw: isRawFile,
            isDng: isDngFile,
            isSelectableRaw: isSelectableRaw,
            hasRawFiles: false,
            rawFileCount: 0
          }
        }

        // Store mapping for quick lookup
        this.nodeMap.set(nodeKey, item)

        if (isFolder && item.children) {
          node.children = this.convertItemsToNodes(item.children, nodeKey)
          // Calculate selectable RAW file statistics for folders (exclude DNG files)
          const selectableRawFiles = this.getFolderSelectableRawFiles(node.children)
          node.data.hasRawFiles = selectableRawFiles.length > 0
          node.data.rawFileCount = selectableRawFiles.length
        }

        return node
      })
    },

    async getFileTree(path) {
      try {
        const response = await axios.get(`${this.apiBaseUrl}/file-tree?path=${encodeURIComponent(path)}`)
        return response.data
      } catch (error) {
        return []
      }
    },

    async onNodeExpand(node) {
      // Set expanded state immediately (for UI responsiveness)
      this.expandedKeys = { ...this.expandedKeys, [node.key]: true }

      // Load children if not already loaded
      if (node.type === 'folder' && node.data.originalData && !node.data.originalData.children) {
        // Store current scroll position before making changes
        let scrollTop = 0
        try {
          const scrollContainer = this.$el?.querySelector?.('.file-tree-container')
          scrollTop = scrollContainer?.scrollTop || 0
        } catch (error) {
          console.warn('Could not access scroll container:', error)
        }

        const children = await this.getFileTree(node.data.originalData.path)
        node.data.originalData.children = children
        
        // Convert new children to tree nodes
        node.children = this.convertItemsToNodes(children, node.key)
        
        // Update RAW file statistics
        const rawFiles = this.getFolderRawFiles(node.children)
        node.data.hasRawFiles = rawFiles.length > 0
        node.data.rawFileCount = rawFiles.length

        // Instead of forcing a full re-render, trigger Vue reactivity more gently
        this.$forceUpdate()
        
        // Restore scroll position after Vue has processed the update
        this.$nextTick(() => {
          setTimeout(() => {
            try {
              const newScrollContainer = this.$el?.querySelector?.('.file-tree-container')
              if (newScrollContainer && scrollTop > 0) {
                newScrollContainer.scrollTop = scrollTop
              }
            } catch (error) {
              console.warn('Could not restore scroll position:', error)
            }
          }, 100)
        })
      }
    },

    onNodeCollapse(node) {
      this.expandedKeys = { ...this.expandedKeys }
      delete this.expandedKeys[node.key]
    },

    async onNodeSelect(node) {
      if (node.type === 'file' && node.data.isSelectableRaw) {
        this.$emit('file-selected', node.data.originalData)
      } else if (node.type === 'folder') {
        // Select all non-DNG RAW files in this folder and subfolders
        await this.selectAllRawFilesInFolder(node, true)
      }
    },

    async onNodeUnselect(node) {
      if (node.type === 'file' && node.data.isSelectableRaw) {
        this.$emit('file-deselected', node.data.originalData)
      } else if (node.type === 'folder') {
        // Deselect all non-DNG RAW files in this folder and subfolders
        await this.selectAllRawFilesInFolder(node, false)
      }
    },

    updateSelectionFromProps(selectedFiles) {
      const newSelectionKeys = {}
      
      // Create selection keys based on selectedFiles prop
      selectedFiles.forEach(file => {
        // Find the corresponding node key for this file
        for (let [nodeKey, originalData] of this.nodeMap) {
          if (originalData.path === file.path) {
            newSelectionKeys[nodeKey] = { checked: true, partialChecked: false }
            break
          }
        }
      })
      
      this.selectionKeys = newSelectionKeys
    },

    isRawFile(filename) {
      const ext = filename.toLowerCase().split('.').pop()
      const rawExtensions = ['raw', 'nef', 'cr2', 'arw', 'dng', 'raf', 'orf', 'rw2', 'pef', 'srw']
      return rawExtensions.includes(ext)
    },

    isDngFile(filename) {
      const ext = filename.toLowerCase().split('.').pop()
      return ext === 'dng'
    },

    isSelectableRawFile(filename) {
      // DNG files are not selectable (they are processed output files)
      return this.isRawFile(filename) && !this.isDngFile(filename)
    },

    getThumbnailUrl(filePath) {
      // Get relative path from the base folder
      const relativePath = this.getRelativePath(filePath)
      return `${this.apiBaseUrl}/thumbnail/${relativePath}`
    },

    getThumbnailUrlWithAuth(filePath) {
      // Get relative path from the base folder
      const relativePath = this.getRelativePath(filePath)
      const token = localStorage.getItem('authToken')
      return `${this.apiBaseUrl}/thumbnail/${relativePath}?token=${encodeURIComponent(token)}`
    },

    getLargePreviewUrl(filePath) {
      // Get relative path from the base folder
      const relativePath = this.getRelativePath(filePath)
      return `${this.apiBaseUrl}/large-preview/${relativePath}`
    },

    getLargePreviewUrlWithAuth(filePath) {
      // Get relative path from the base folder
      const relativePath = this.getRelativePath(filePath)
      const token = localStorage.getItem('authToken')
      return `${this.apiBaseUrl}/large-preview/${relativePath}?token=${encodeURIComponent(token)}`
    },

    getRelativePath(fullPath) {
      if (!this.config) return fullPath
      return fullPath.replace(this.config.base_image_input_folder, '').replace(/^[\\\/]/, '') || '/'
    },

    getDisplayFilename(filename, isDng) {
      if (!isDng) {
        return filename
      }
      
      // For DNG files, truncate after 8 characters with ellipsis
      if (filename.length > 8) {
        return filename.substring(0, 8) + '...'
      }
      
      return filename
    },

    onThumbnailError(event) {
      // Hide the thumbnail and show the file icon instead
      const img = event.target
      img.style.display = 'none'
      
      // Show fallback icon
      const container = img.parentElement
      const fallbackIcon = document.createElement('i')
      fallbackIcon.className = 'fas fa-image image-icon'
      container.appendChild(fallbackIcon)
    },

    onThumbnailLoad(event) {
      // Thumbnail loaded successfully
      const img = event.target
      img.style.opacity = '1'
    },

    // Helper methods for folder-level selection
    getFolderRawFiles(children) {
      return children.filter(child => child.type === 'file' && child.data.isRaw)
    },

    getFolderSelectableRawFiles(children) {
      return children.filter(child => child.type === 'file' && child.data.isSelectableRaw)
    },

    areAllFolderFilesSelected(folderNode) {
      const selectableRawFiles = this.getFolderSelectableRawFiles(folderNode.children || [])
      return selectableRawFiles.length > 0 && selectableRawFiles.every(fileNode => 
        this.selectionKeys[fileNode.key] && this.selectionKeys[fileNode.key].checked
      )
    },

    areSomeFolderFilesSelected(folderNode) {
      const selectableRawFiles = this.getFolderSelectableRawFiles(folderNode.children || [])
      return selectableRawFiles.some(fileNode => 
        this.selectionKeys[fileNode.key] && this.selectionKeys[fileNode.key].checked
      )
    },

    toggleAllFolderFiles(folderNode, selectAll) {
      const selectableRawFiles = this.getFolderSelectableRawFiles(folderNode.children || [])
      
      selectableRawFiles.forEach(fileNode => {
        const isCurrentlySelected = this.selectionKeys[fileNode.key] && this.selectionKeys[fileNode.key].checked
        
        if (selectAll && !isCurrentlySelected) {
          this.$emit('file-selected', fileNode.data.originalData)
        } else if (!selectAll && isCurrentlySelected) {
          this.$emit('file-deselected', fileNode.data.originalData)
        }
      })
    },

    async selectAllRawFilesInFolder(folderNode, select) {
      // Load children if not already loaded
      if (!folderNode.children && folderNode.data && folderNode.data.originalData) {
        // Store current scroll position before making changes
        let scrollTop = 0
        try {
          const scrollContainer = this.$el?.querySelector?.('.file-tree-container')
          scrollTop = scrollContainer?.scrollTop || 0
        } catch (error) {
          console.warn('Could not access scroll container:', error)
        }

        try {
          const children = await this.getFileTree(folderNode.data.originalData.path)
          folderNode.data.originalData.children = children
          folderNode.children = this.convertItemsToNodes(children, folderNode.key)
          
          // Update RAW file statistics
          const selectableRawFiles = this.getFolderSelectableRawFiles(folderNode.children)
          folderNode.data.hasRawFiles = selectableRawFiles.length > 0
          folderNode.data.rawFileCount = selectableRawFiles.length
          
          // Use gentle update instead of forcing full re-render
          this.$forceUpdate()
          
          // Restore scroll position after Vue has processed the update
          this.$nextTick(() => {
            setTimeout(() => {
              try {
                const newScrollContainer = this.$el?.querySelector?.('.file-tree-container')
                if (newScrollContainer && scrollTop > 0) {
                  newScrollContainer.scrollTop = scrollTop
                }
              } catch (error) {
                console.warn('Could not restore scroll position:', error)
              }
            }, 100)
          })
        } catch (error) {
          console.error('Failed to load folder contents:', error)
          return
        }
      }

      // Recursively select/deselect all selectable RAW files in this folder and subfolders
      if (folderNode.children) {
        folderNode.children.forEach(childNode => {
          if (childNode.type === 'file' && childNode.data.isSelectableRaw) {
            // This is a selectable RAW file
            const isCurrentlyInSelectedFiles = this.selectedFiles.some(file => file.path === childNode.data.originalData.path)
            
            if (select && !isCurrentlyInSelectedFiles) {
              this.$emit('file-selected', childNode.data.originalData)
            } else if (!select && isCurrentlyInSelectedFiles) {
              this.$emit('file-deselected', childNode.data.originalData)
            }
          } else if (childNode.type === 'folder') {
            // Recursively process subfolders
            this.selectAllRawFilesInFolder(childNode, select)
          }
        })
      }
    },

    refresh() {
      this.loadFileTree()
    },

    async refreshPreservingState() {
      // Store current scroll position
      let scrollTop = 0
      try {
        const scrollContainer = this.$el?.querySelector?.('.file-tree-container')
        scrollTop = scrollContainer?.scrollTop || 0
      } catch (error) {
        // Ignore scroll position errors
      }
      
      // Store current expanded folders
      const currentExpandedKeys = { ...this.expandedKeys }
      
      try {
        // Set loading state
        this.isLoading = true
        
        // Clear the file tree completely and reload
        this.fileTree = []
        this.treeNodes = []
        this.expandedKeys = {}
        this.nodeMap.clear()
        
        // Force a re-render
        this.fileTreeKey = Date.now()
        await this.$nextTick()
        
        // Load fresh file tree data
        await this.loadFileTree()
        
        // Wait for the new tree to fully render
        await this.$nextTick()
        
        // Restore expanded folders by path matching
        for (const [nodeKey, expanded] of Object.entries(currentExpandedKeys)) {
          if (expanded) {
            // Find corresponding node in new tree and expand it
            const node = this.findNodeByPath(this.treeNodes, nodeKey)
            if (node) {
              await this.onNodeExpand(node)
              await new Promise(resolve => setTimeout(resolve, 50))
            }
          }
        }
        
        // Final re-render
        this.fileTreeKey = Date.now()
        await this.$nextTick()
        
        // Restore scroll position
        if (scrollTop > 0) {
          setTimeout(() => {
            try {
              const scrollContainer = this.$el?.querySelector?.('.file-tree-container')
              if (scrollContainer) {
                scrollContainer.scrollTop = scrollTop
              }
            } catch (error) {
              // Ignore scroll restoration errors
            }
          }, 500)
        }
        
      } catch (error) {
        // Fallback: just reload without preservation
        await this.loadFileTree()
      } finally {
        this.isLoading = false
      }
    },

    findNodeByPath(nodes, targetPath) {
      for (const node of nodes) {
        if (node.key === targetPath) {
          return node
        }
        if (node.children) {
          const found = this.findNodeByPath(node.children, targetPath)
          if (found) return found
        }
      }
      return null
    },

    navigateToRoot() {
      if (this.config) {
        this.currentPath = this.config.base_image_input_folder
        this.loadFileTree()
      }
    },

    // Image preview modal methods
    showImagePreview(fileData) {
      this.previewImageData = fileData
      this.previewImageLoading = true
      this.showPreviewModal = true
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    },

    closeImagePreview() {
      this.showPreviewModal = false
      this.previewImageData = null
      this.previewImageLoading = false
      
      // Restore body scroll
      document.body.style.overflow = ''
    },

    onPreviewImageLoad() {
      this.previewImageLoading = false
    },

    onPreviewImageError() {
      this.previewImageLoading = false
      this.$emit('error', 'Failed to load image preview')
    },

    handleKeydown(event) {
      // Close modal on ESC key
      if (event.key === 'Escape' && this.showPreviewModal) {
        this.closeImagePreview()
      }
    }
  }
}
</script>

<style scoped>
.file-browser {
  height: 100%;
}

.form-check-input {
    margin-top: 0px!important;
}

.card-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--surface-border);
  background: var(--surface-50);
}

.browser-content {
  height: 500px;
  overflow: hidden;
}

.file-tree-container {
  height: 100%;
  overflow-y: auto;
  padding: 0.5rem;
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.processing-spinner {
  text-align: center;
}

.file-icon-container {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-thumbnail {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 2px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.clickable-thumbnail {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-thumbnail:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.folder-icon {
  color: #ffc107;
}

.image-icon {
  color: #28a745;
}



/* DNG file styling */
.dng-file-disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
}

.dng-file-disabled .file-thumbnail {
  filter: grayscale(50%);
  cursor: not-allowed !important;
}

.dng-file-disabled .file-thumbnail:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* File info container styling */
.file-info-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* PrimeVue Tree styling adjustments */
:deep(.p-tree) {
  border: none;
  background: transparent;
  padding: 0;
}

:deep(.p-tree .p-tree-container) {
  padding: 0;
}

:deep(.p-treenode) {
  padding: 0.25rem 0;
}

:deep(.p-treenode-content) {
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.15s;
}

:deep(.p-treenode-content:hover) {
  background-color: #f8f9fa;
}

:deep(.p-checkbox) {
  margin-right: 0.5rem;
}

:deep(.p-treenode-icon) {
  margin-right: 0.5rem;
  color: #6c757d;
}

/* Disable selection for DNG files */
:deep(.dng-file-disabled .p-checkbox) {
  display: none !important;
}

:deep(.dng-file-disabled .p-treenode-content) {
  cursor: not-allowed !important;
  background-color: transparent !important;
}

:deep(.dng-file-disabled .p-treenode-content:hover) {
  background-color: transparent !important;
}


</style>

<!-- Global styles for modal (since it's teleported to body) -->
<style>
/* Image Preview Modal Global Styles */
.modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.8) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 9999 !important;
  padding: 1rem !important;
  margin: 0 !important;
}

.modal-container {
  background: white !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
  max-width: 90vw !important;
  max-height: 90vh !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.modal-header {
  padding: 1rem 1.5rem !important;
  border-bottom: 1px solid #dee2e6 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  background: #f8f9fa !important;
}

.modal-title {
  margin: 0 !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  color: #495057 !important;
  display: flex !important;
  align-items: center !important;
}

.btn-close {
  background: none !important;
  border: none !important;
  font-size: 1.2rem !important;
  cursor: pointer !important;
  color: #6c757d !important;
  padding: 0.5rem !important;
  border-radius: 4px !important;
  transition: all 0.2s ease !important;
}

.btn-close:hover {
  background: #e9ecef !important;
  color: #495057 !important;
}

.modal-body {
  flex: 1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 1rem !important;
  min-height: 200px !important;
  position: relative !important;
}

.image-container {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
}

.preview-image {
  max-width: 100% !important;
  max-height: 70vh !important;
  object-fit: contain !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
}

.loading-spinner {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 2rem !important;
  color: #6c757d !important;
}

.modal-footer {
  padding: 1rem 1.5rem !important;
  border-top: 1px solid #dee2e6 !important;
  background: #f8f9fa !important;
}

.image-info {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  font-size: 0.875rem !important;
}

@media (max-width: 768px) {
  .modal-container {
    margin: 0.5rem !important;
    max-width: calc(100vw - 1rem) !important;
    max-height: calc(100vh - 1rem) !important;
  }
  
  .preview-image {
    max-height: 70vh !important;
  }
}
</style>
